import type {
  ModelCapabilities,
  ProviderKind,
  ProviderModelOptions,
  ScopedThreadRef,
  ServerProviderModel,
} from "@cmmd-forge/contracts";
import { memo } from "react";

import type { DraftId } from "../../draftId";
import { getProviderModelCapabilities } from "../../providerModels";
import { ComposerControlSeparator } from "./ComposerControl";
import { TraitsPicker } from "./TraitsPicker";

/**
 * One effort level in the shape the picker menu reads.
 *
 * t3code keys its options by `id` and carries the default flag on the option
 * itself; Forge's `EffortOption` keys by `value`. This is the whole difference
 * between the two, so the adapter below is the only place either name has to
 * be spelled, and the rest of the Composer can stay on one shape.
 */
export interface ReasoningEffortOption {
  readonly id: string;
  readonly label: string;
  readonly isDefault: boolean;
  /**
   * True for a level Forge applies by prefixing the Draft's text (Claude's
   * "ultrathink") rather than by sending an effort parameter. Picking one
   * rewrites the prompt, so the menu treats it differently from the rest.
   */
  readonly promptInjected: boolean;
}

/** Turn a model's capability descriptor into the picker's option list. */
export function buildReasoningEffortOptions(
  caps: ModelCapabilities,
): ReadonlyArray<ReasoningEffortOption> {
  return caps.reasoningEffortLevels.map((level) => ({
    id: level.value,
    label: level.label,
    isDefault: level.isDefault === true,
    promptInjected: caps.promptInjectedEffortLevels.includes(level.value),
  }));
}

/**
 * Whether the picker has anything to offer for the selected model.
 *
 * Effort is the headline, but the same menu also carries the thinking toggle,
 * fast mode and the context window, and a model can have one of those without
 * having any effort levels at all. Hiding on "no effort levels" alone would
 * take the thinking toggle away from every Claude model that has one, so the
 * test is "does the menu have a single row", which is what decides whether the
 * trigger would open onto nothing.
 */
export function shouldRenderReasoningEffortPicker(caps: ModelCapabilities): boolean {
  return (
    buildReasoningEffortOptions(caps).length > 0 ||
    caps.supportsThinkingToggle ||
    caps.supportsFastMode ||
    caps.contextWindowOptions.length > 1
  );
}

interface ReasoningEffortPickerProps {
  readonly provider: ProviderKind;
  readonly threadRef?: ScopedThreadRef;
  readonly draftId?: DraftId;
  readonly model: string;
  readonly models: ReadonlyArray<ServerProviderModel>;
  readonly modelOptions: ProviderModelOptions[ProviderKind] | undefined;
  readonly prompt: string;
  readonly onPromptChange: (prompt: string) => void;
}

/**
 * The Expanded Composer footer's reasoning effort control, beside the model
 * picker rather than inside it.
 *
 * Two things live here that the underlying menu cannot decide for itself. The
 * first is visibility: a model with no options would otherwise render a trigger
 * with an empty label, which reads as a broken control and has no accessible
 * name. The second is the leading separator, which belongs to the control it
 * introduces so that hiding one hides both.
 */
export const ReasoningEffortPicker = memo(function ReasoningEffortPicker({
  provider,
  threadRef,
  draftId,
  model,
  models,
  modelOptions,
  prompt,
  onPromptChange,
}: ReasoningEffortPickerProps) {
  const caps = getProviderModelCapabilities(models, model, provider);
  if (!shouldRenderReasoningEffortPicker(caps)) {
    return null;
  }
  return (
    <>
      <ComposerControlSeparator />
      <TraitsPicker
        provider={provider}
        models={models}
        {...(threadRef ? { threadRef } : {})}
        {...(draftId ? { draftId } : {})}
        model={model}
        modelOptions={modelOptions}
        prompt={prompt}
        onPromptChange={onPromptChange}
      />
    </>
  );
});
